import { useEffect, useMemo, useRef, useState } from 'react';
import { ProjectMap } from '@/components/project/project-map';
import { processGeocodingResult } from '@/utils/locationUtils';
import type { Story } from '@/types/cmsTypes';

interface Props {
  latitude: string;
  longitude: string;
  address: string;
  title?: string;
  onChange: (patch: { address?: string; latitude?: string; longitude?: string; city?: string }) => void;
}

function geocode(request: google.maps.GeocoderRequest): Promise<google.maps.GeocoderResult[]> {
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(request, (results, status) => {
      if (status === 'OK' && results) resolve(results);
      else reject(new Error(`Geocoding failed: ${status}`));
    });
  });
}

export function PublishLocationMap({ latitude, longitude, address, title, onChange }: Props) {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  const hasCoords = latitude !== '' && longitude !== '' && !Number.isNaN(lat) && !Number.isNaN(lng);

  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const autoFilledKeyRef = useRef<string | null>(null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const check = () => {
      attempts++;
      if (typeof google !== 'undefined' && google.maps?.Geocoder) {
        setIsGoogleMapsLoaded(true);
      } else if (attempts < 50) {
        timeoutId = setTimeout(check, 100);
      }
    };
    check();
    return () => clearTimeout(timeoutId);
  }, []);

  // Immersal/geospatial scenes carry anchor coordinates but often an empty
  // scene_origin_location_info, so the address would arrive blank. Reverse-geocode
  // the seeded coordinates to fill it. Keyed on coords so it runs once and respects
  // a manually cleared address.
  useEffect(() => {
    if (!isGoogleMapsLoaded || !hasCoords || address.trim() !== '') return;
    const key = `${lat},${lng}`;
    if (autoFilledKeyRef.current === key) return;
    autoFilledKeyRef.current = key;
    let cancelled = false;
    geocode({ location: { lat, lng } })
      .then((results) => {
        const info = results[0] ? processGeocodingResult(results[0]) : null;
        if (!cancelled && info?.address) onChange({ address: info.address, city: info.city ?? '' });
      })
      .catch((e) => console.error('Reverse geocode failed:', e));
    return () => {
      cancelled = true;
    };
    // onChange is an unstable inline parent closure; the coords/address guards above
    // prevent re-runs, so it is intentionally excluded from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleMapsLoaded, hasCoords, address, lat, lng]);

  const stories = useMemo<Story[]>(
    () => [
      {
        id: 'publish-location',
        projectId: '',
        title: title || 'Location',
        description: '',
        image: '',
        order: 1,
        status: 'draft',
        createdAt: new Date(0),
        updatedAt: new Date(0),
        locationInfo: hasCoords ? { address: address || '', coordinates: { lat, lng } } : null,
      },
    ],
    [title, address, hasCoords, lat, lng],
  );

  const handleMapClick = async (clickLat: number, clickLng: number) => {
    onChange({ latitude: String(clickLat), longitude: String(clickLng) });
    try {
      const results = await geocode({ location: { lat: clickLat, lng: clickLng } });
      if (results[0]) {
        const info = processGeocodingResult(results[0]);
        onChange({ address: info.address || '', city: info.city ?? '' });
      }
    } catch (e) {
      console.error('Reverse geocode failed:', e);
    }
  };

  return (
    <ProjectMap
      stories={stories}
      allowMapClick
      onMapClick={handleMapClick}
      centerLocation={hasCoords ? { lat, lng } : undefined}
      isStoryEditPage
      hideNumbers
      hideLegend
      heightClass="h-full"
    />
  );
}
